import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Typography } from '@/components/ui/typography'
import { useAppForm } from '@/lib/form'
import { useStore } from '@tanstack/react-form'
import { Check } from 'lucide-react'
import { z } from 'zod/v4'
import NavigationCollectionPicker from './NavigationCollectionPicker'
import {
  collectNavigationUsage,
  createNavigationDraftId,
  findNavigationCollectionItem,
  getNavigationItem,
  getNavigationMoveConsequences,
  isSafeNavigationDestination,
  isShopAllDestination,
  NAVIGATION_DESTINATION_MAX_LENGTH,
  NAVIGATION_ITEM_TYPE,
  navigationItemLabel,
  navigationPlacementLabel,
  TOP_LEVEL_PLACEMENT,
} from './navigationMenuEditor.utils'

function AlreadyAddedIndicator({ message }) {
  return (
    <span className="inline-flex items-center text-success" aria-label={message}>
      <Check className="size-3" />
    </span>
  )
}

const navigationItemSchema = z
  .object({
    id: z.union([z.string(), z.number()]),
    type: z.enum([
      NAVIGATION_ITEM_TYPE.COLLECTION,
      NAVIGATION_ITEM_TYPE.LINK,
      NAVIGATION_ITEM_TYPE.MENU,
      NAVIGATION_ITEM_TYPE.SHOP_ALL,
      NAVIGATION_ITEM_TYPE.OCCASIONS,
    ]),
    label: z.string().trim().max(120).nullable(),
    href: z.string().trim().max(NAVIGATION_DESTINATION_MAX_LENGTH).nullable(),
    collection_id: z.number().positive().nullable(),
    placement: z.string(),
  })
  .superRefine((value, context) => {
    if (
      [NAVIGATION_ITEM_TYPE.LINK, NAVIGATION_ITEM_TYPE.MENU].includes(value.type) &&
      !value.label
    ) {
      context.addIssue({ code: 'custom', path: ['label'], message: 'Enter a label.' })
    }

    if (value.type === NAVIGATION_ITEM_TYPE.LINK) {
      if (!isSafeNavigationDestination(value.href)) {
        context.addIssue({
          code: 'custom',
          path: ['href'],
          message: 'Use an internal path beginning with / or a full http:// or https:// URL.',
        })
      } else if (isShopAllDestination(value.href)) {
        context.addIssue({
          code: 'custom',
          path: ['href'],
          message: 'Use the Shop All menu item for this destination.',
        })
      }
    }

    if (value.type === NAVIGATION_ITEM_TYPE.COLLECTION && !value.collection_id) {
      context.addIssue({ code: 'custom', path: ['collection_id'], message: 'Select a collection.' })
    }

    if (
      [NAVIGATION_ITEM_TYPE.MENU, NAVIGATION_ITEM_TYPE.OCCASIONS].includes(value.type) &&
      value.placement !== TOP_LEVEL_PLACEMENT
    ) {
      context.addIssue({
        code: 'custom',
        path: ['placement'],
        message: 'This item must stay in the top navigation.',
      })
    }
  })

function defaultValues(item, parentId) {
  return {
    id: item?.id ?? createNavigationDraftId(),
    type: item?.type ?? NAVIGATION_ITEM_TYPE.COLLECTION,
    label: item?.label ?? null,
    href: item?.href ?? null,
    collection_id: item?.collection_id ? Number(item.collection_id) : null,
    placement: parentId === null ? TOP_LEVEL_PLACEMENT : String(parentId),
  }
}

function itemFromValues(values, existingItem, selectedCollection) {
  const collectionId =
    values.type === NAVIGATION_ITEM_TYPE.COLLECTION ? Number(values.collection_id) : null
  const changesCollection =
    values.type === NAVIGATION_ITEM_TYPE.COLLECTION &&
    Number(existingItem?.collection_id) !== collectionId
  const nextItem = {
    ...existingItem,
    id: existingItem?.id ?? values.id,
    type: values.type,
    label:
      values.type === NAVIGATION_ITEM_TYPE.COLLECTION
        ? (existingItem?.label ?? null)
        : values.type === NAVIGATION_ITEM_TYPE.SHOP_ALL && !values.label
          ? null
          : values.label?.trim() || null,
    href: values.type === NAVIGATION_ITEM_TYPE.LINK ? values.href?.trim() || null : null,
    collection_id: collectionId,
    is_enabled:
      changesCollection && selectedCollection?.is_published === false
        ? false
        : (existingItem?.is_enabled ?? true),
    props: existingItem?.props ?? {},
    children:
      values.type === NAVIGATION_ITEM_TYPE.MENU ? (existingItem?.children ?? []) : undefined,
  }

  if (
    values.type === NAVIGATION_ITEM_TYPE.COLLECTION &&
    Number(existingItem?.collection_id) !== collectionId
  ) {
    delete nextItem.collection
  }

  return nextItem
}

export default function NavigationMenuItemDialog({
  item = null,
  parentId = null,
  items,
  collections,
  onSave,
  onClose,
}) {
  const isEditing = item !== null
  const usage = collectNavigationUsage(items, item?.id)
  const groups = items.filter((candidate) => candidate.type === NAVIGATION_ITEM_TYPE.MENU)
  const typeOptions = [
    { id: NAVIGATION_ITEM_TYPE.COLLECTION, name: 'Collection' },
    { id: NAVIGATION_ITEM_TYPE.LINK, name: 'Custom link' },
    ...(parentId === null || isEditing
      ? [
          { id: NAVIGATION_ITEM_TYPE.MENU, name: 'Dropdown menu' },
          { id: NAVIGATION_ITEM_TYPE.SHOP_ALL, name: 'Shop All' },
          { id: NAVIGATION_ITEM_TYPE.OCCASIONS, name: 'Occasions' },
        ]
      : [{ id: NAVIGATION_ITEM_TYPE.SHOP_ALL, name: 'Shop All' }]),
  ].map((option) => {
    const alreadyAdded = usage.systemTypes.has(option.id)
    const message = alreadyAdded ? `${option.name} is already in this menu.` : null

    return {
      ...option,
      disabled: alreadyAdded,
      tooltip: message,
      tooltipAnchor: 'end',
      tooltipClassName: 'max-w-64',
      end: message ? <AlreadyAddedIndicator message={message} /> : null,
    }
  })
  const placementOptions = [
    { id: TOP_LEVEL_PLACEMENT, name: 'Top navigation' },
    ...groups
      .filter((group) => String(group.id) !== String(item?.id))
      .map((group) => ({ id: String(group.id), name: group.label || 'Dropdown menu' })),
  ]
  const collectionsById = new Map(
    collections.map((collection) => [Number(collection.id), collection])
  )

  const form = useAppForm({
    defaultValues: defaultValues(item, parentId),
    validators: { onSubmit: navigationItemSchema },
    onSubmit: ({ value }) => {
      const selectedLocation =
        value.type === NAVIGATION_ITEM_TYPE.COLLECTION
          ? findNavigationCollectionItem(items, value.collection_id)
          : null
      const isMovingExistingItem =
        selectedLocation && String(selectedLocation.item.id) !== String(item?.id)
      const sourceItem = isMovingExistingItem ? selectedLocation.item : item

      onSave({
        item: itemFromValues(value, sourceItem, collectionsById.get(Number(value.collection_id))),
        parentId: value.placement === TOP_LEVEL_PLACEMENT ? null : value.placement,
        previousId: sourceItem?.id ?? null,
        replacedItemId: isMovingExistingItem ? (item?.id ?? null) : null,
        append: Boolean(isMovingExistingItem),
      })
      onClose()
    },
  })
  const type = useStore(form.store, (state) => state.values.type)
  const placement = useStore(form.store, (state) => state.values.placement)
  const isTopLevelOnly = [NAVIGATION_ITEM_TYPE.MENU, NAVIGATION_ITEM_TYPE.OCCASIONS].includes(type)
  const selectedCollectionId = useStore(form.store, (state) => state.values.collection_id)
  const selectedCollectionLocation =
    type === NAVIGATION_ITEM_TYPE.COLLECTION
      ? findNavigationCollectionItem(items, selectedCollectionId)
      : null
  const isMovingExistingCollection =
    selectedCollectionLocation && String(selectedCollectionLocation.item.id) !== String(item?.id)
  const itemLocation = item ? getNavigationItem(items, item.id) : null
  const sourceLocation = isMovingExistingCollection
    ? selectedCollectionLocation
    : (itemLocation ?? selectedCollectionLocation)
  const destinationParentId = placement === TOP_LEVEL_PLACEMENT ? null : placement
  const sourceLabel = sourceLocation
    ? navigationPlacementLabel(items, sourceLocation.parentId)
    : null
  const destinationLabel = navigationPlacementLabel(items, destinationParentId)
  const { emptiedGroupIds } = getNavigationMoveConsequences(items, {
    previousId: sourceLocation?.item.id ?? null,
    replacedItemId: isMovingExistingCollection ? (item?.id ?? null) : null,
    parentId: destinationParentId,
  })
  const emptiedGroups = emptiedGroupIds.map((groupId) => ({
    id: groupId,
    label: navigationPlacementLabel(items, groupId),
  }))

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit menu item' : 'Add menu item'}</DialogTitle>
          <DialogDescription>
            Choose what shoppers see and where it appears in your navigation.
          </DialogDescription>
        </DialogHeader>

        <form.AppForm>
          <div className="space-y-5 py-2">
            <form.AppField name="type">
              {(field) => (
                <field.SelectInput
                  label="Item type"
                  options={typeOptions}
                  disabled={isEditing}
                  onValueChange={(nextType) => {
                    if (
                      [NAVIGATION_ITEM_TYPE.MENU, NAVIGATION_ITEM_TYPE.OCCASIONS].includes(nextType)
                    ) {
                      form.setFieldValue('placement', TOP_LEVEL_PLACEMENT)
                    }
                  }}
                />
              )}
            </form.AppField>

            {type === NAVIGATION_ITEM_TYPE.COLLECTION ? (
              <form.AppField name="collection_id">
                {(field) => (
                  <NavigationCollectionPicker
                    field={field}
                    collections={collections}
                    items={items}
                  />
                )}
              </form.AppField>
            ) : null}

            {isMovingExistingCollection || emptiedGroups.length ? (
              <div className="rounded-md border border-status-info-border bg-status-info-background px-3 py-2">
                <Typography variant="label">
                  {isMovingExistingCollection && item ? (
                    <>
                      {navigationItemLabel(selectedCollectionLocation.item, collectionsById)} will
                      replace {navigationItemLabel(item, collectionsById)}. Its existing menu item
                      and settings will move from{' '}
                      <strong className="font-semibold">{sourceLabel}</strong> to the end of{' '}
                      <strong className="font-semibold">{destinationLabel}</strong>.
                    </>
                  ) : sourceLabel === destinationLabel ? (
                    <>
                      This collection is already in{' '}
                      <strong className="font-semibold">{sourceLabel}</strong>. It will move to the
                      end of that section.
                    </>
                  ) : (
                    <>
                      {type === NAVIGATION_ITEM_TYPE.COLLECTION ? 'This collection' : 'This item'}
                      {' will move from '}
                      <strong className="font-semibold">{sourceLabel}</strong> to{' '}
                      <strong className="font-semibold">{destinationLabel}</strong>.
                    </>
                  )}
                </Typography>
                <Typography variant="caption" className="mt-1 block">
                  {emptiedGroups.length ? (
                    <>
                      {emptiedGroups.map((group, index) => (
                        <span key={group.id}>
                          {index > 0 ? (index === emptiedGroups.length - 1 ? ' and ' : ', ') : null}
                          <strong className="font-semibold">{group.label}</strong>
                        </span>
                      ))}{' '}
                      {emptiedGroups.length === 1
                        ? 'will also be removed because it would be empty.'
                        : 'will also be removed because they would be empty.'}{' '}
                      Your storefront won’t change until you save the Website Builder.
                    </>
                  ) : (
                    <>Your storefront won’t change until you save the Website Builder.</>
                  )}
                </Typography>
              </div>
            ) : null}

            {[NAVIGATION_ITEM_TYPE.LINK, NAVIGATION_ITEM_TYPE.MENU].includes(type) ? (
              <form.AppField name="label">
                {(field) => (
                  <field.TextInput
                    label={type === NAVIGATION_ITEM_TYPE.MENU ? 'Menu name' : 'Link label'}
                    maxLength={120}
                  />
                )}
              </form.AppField>
            ) : null}

            {type === NAVIGATION_ITEM_TYPE.LINK ? (
              <form.AppField name="href">
                {(field) => (
                  <field.TextInput
                    label="Destination"
                    placeholder="/about-us or https://example.com"
                    maxLength={255}
                  />
                )}
              </form.AppField>
            ) : null}

            {[NAVIGATION_ITEM_TYPE.SHOP_ALL, NAVIGATION_ITEM_TYPE.OCCASIONS].includes(type) ? (
              <form.AppField name="label">
                {(field) => (
                  <field.TextInput
                    label="Custom label (optional)"
                    placeholder={type === NAVIGATION_ITEM_TYPE.SHOP_ALL ? 'Shop All' : 'Occasions'}
                    maxLength={120}
                  />
                )}
              </form.AppField>
            ) : null}

            <form.AppField name="placement">
              {(field) => (
                <field.SelectInput
                  label="Place in"
                  options={placementOptions}
                  disabled={isTopLevelOnly}
                />
              )}
            </form.AppField>

            {type === NAVIGATION_ITEM_TYPE.MENU && isEditing && item?.children?.length === 0 ? (
              <p className="text-sm text-status-warning-foreground">
                Add at least one link to this dropdown before saving the website design.
              </p>
            ) : null}
          </div>
        </form.AppForm>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => form.handleSubmit()}
            disabled={isTopLevelOnly && placement !== TOP_LEVEL_PLACEMENT}
          >
            {isMovingExistingCollection
              ? 'Move collection'
              : isEditing
                ? 'Save changes'
                : 'Add item'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
